/**
 * Rede de segurança contra tela branca.
 *
 * Qualquer erro de renderização não tratado derruba a árvore inteira do React e
 * o usuário fica olhando uma tela em branco. Aqui o erro é contido: o cliente vê
 * um aviso amistoso com um código curto, e o rastro técnico vai para o backend
 * (/admin/erros) sem aparecer na tela.
 */
import { Component, Fragment } from "react";
import { RotateCcw, LifeBuoy } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Erro causado por algo de FORA do app mexendo no DOM (tradutor do navegador,
 * extensão, leitor de tela). O React perde a referência do nó e quebra a
 * árvore toda. Não é bug de tela: remontar resolve, e mostrar a tela de erro
 * só assusta o usuário.
 */
const DOM_EXTERNO = /(removeChild|insertBefore)[\s\S]*not a child of this node/i;

// Um registro por mensagem por sessão: estes erros vêm em rajada (um por
// interação) e inundavam o painel de /admin/erros com centenas de repetições.
const jaRegistrados = new Set();

/** O tradutor do Chrome marca o html e troca os textos por <font>. */
function paginaTraduzida() {
  try {
    const cls = document.documentElement.className || "";
    return (
      /translated-(ltr|rtl)/.test(cls) ||
      !!document.querySelector("font[style], .notranslate font, body font")
    );
  } catch {
    return false;
  }
}

function gerarCodigo() {
  return `ERR-${Math.random().toString(36).slice(2, 6).toUpperCase()}${Date.now()
    .toString(36)
    .slice(-3)
    .toUpperCase()}`;
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { erro: null, codigo: null, externo: false, chave: 0, tentativas: 0 };
  }

  static getDerivedStateFromError(erro) {
    return {
      erro,
      codigo: gerarCodigo(),
      externo: DOM_EXTERNO.test(String(erro?.message || erro)),
    };
  }

  componentDidCatch(erro, info) {
    const codigo = this.state.codigo || gerarCodigo();
    const mensagem = String(erro?.message || erro).slice(0, 600);
    const externo = DOM_EXTERNO.test(mensagem);
    // Teto de remontagens: se a interferência continuar, mostra a tela de erro
    // em vez de ficar remontando para sempre.
    const podeRecuperar = externo && (this.state.tentativas || 0) < 3;
    console.error(`Erro de renderização [${codigo}]:`, erro, info?.componentStack);
    if (jaRegistrados.has(mensagem)) {
      if (podeRecuperar) this.recuperar();
      else if (externo) this.setState({ externo: false });
      return;
    }
    jaRegistrados.add(mensagem);
    try {
      fetch(`${API_URL}/api/frontend-errors`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codigo,
          mensagem,
          stack: String(erro?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 4000),
          url: window.location.href,
          rota: window.location.pathname,
          user_agent: navigator.userAgent,
          pagina_traduzida: paginaTraduzida(),
          idioma_navegador: navigator.language || "",
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* registrar o erro nunca pode gerar outro erro */
    }
    // Interferência externa no DOM: remonta a tela em vez de culpar o usuário.
    if (podeRecuperar) this.recuperar();
    else if (externo) this.setState({ externo: false });
  }

  recuperar = () =>
    this.setState((s) => ({
      erro: null,
      codigo: null,
      externo: false,
      chave: s.chave + 1,
      tentativas: (s.tentativas || 0) + 1,
    }));

  tentarDeNovo = () => this.recuperar();

  render() {
    // A tela de erro apareceria por um instante antes da remontagem.
    if (this.state.erro && this.state.externo) return null;
    if (!this.state.erro) return <Fragment key={this.state.chave}>{this.props.children}</Fragment>;
    return (
      <div
        className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-6 py-12"
        data-testid="error-boundary"
      >
        <div
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full opacity-50 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--accent-light, #dbeafe), transparent 70%)" }}
        />
        <div className="relative w-full max-w-md text-center">
          <svg
            viewBox="0 0 220 150"
            className="mx-auto mb-7 h-36 w-auto"
            aria-hidden="true"
            fill="none"
          >
            <rect x="26" y="18" width="168" height="104" rx="12" fill="#fff" stroke="#e2e8f0" strokeWidth="2" />
            <path d="M26 40h168" stroke="#e2e8f0" strokeWidth="2" />
            <circle cx="42" cy="29" r="3.5" fill="#fecdd3" />
            <circle cx="54" cy="29" r="3.5" fill="#fde68a" />
            <circle cx="66" cy="29" r="3.5" fill="#bbf7d0" />
            <path
              d="M86 92a24 24 0 0 1 48 0"
              stroke="#cbd5e1"
              strokeWidth="3.4"
              strokeLinecap="round"
              strokeDasharray="7 7"
            />
            <path
              d="M96 92a14 14 0 0 1 28 0"
              stroke="#94a3b8"
              strokeWidth="3.4"
              strokeLinecap="round"
            />
            <circle cx="110" cy="92" r="4" fill="#94a3b8" />
            <path d="M86 134h48" stroke="#cbd5e1" strokeWidth="6" strokeLinecap="round" />
            <path d="M110 122v10" stroke="#cbd5e1" strokeWidth="6" strokeLinecap="round" />
          </svg>

          <h1 className="text-xl font-semibold text-slate-900">
            Não conseguimos carregar esta tela agora
          </h1>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
            Pode ter sido uma oscilação momentânea de conexão. Seus dados estão
            salvos — é só tentar abrir novamente.
          </p>

          <div className="mt-7 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.tentarDeNovo}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full px-5 text-sm font-medium text-white transition-colors sm:w-auto"
              style={{ backgroundColor: "var(--accent, #0f172a)" }}
              data-testid="error-boundary-retry"
            >
              <RotateCcw size={15} /> Tentar de novo
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 sm:w-auto"
              data-testid="error-boundary-reload"
            >
              Recarregar a página
            </button>
          </div>

          <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <LifeBuoy size={13} />
            Se continuar, informe este código ao suporte:
            <span className="font-mono font-semibold text-slate-500" data-testid="error-boundary-code">
              {this.state.codigo}
            </span>
          </p>
        </div>
      </div>
    );
  }
}
